import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
	AccessRight,
	IMenuAccessRightModel,
} from "models/MenuAccessRightModel";
import { RootState } from "redux/store";
import { AppRoutings } from "utility/enums/app-routings";
import { AccessRightName } from "utility/enums/user-access-right";
import {
	ReqConversionToBidActionType,
	RequisitionActionType,
} from "utility/enums/requisition-status";
import { BudgetAllocationActionType } from "utility/enums/budget-allocation";
import { UserType } from "utility/enums/user-type";
import { Shared } from "utility/shared";
import { BidActionType } from "utility/enums/bid-detail";
import { ContractActionType } from "utility/enums/contract-detail";
import { OrganizationDetail } from "models/OrganizationDetail";
import { defaultDivisionGridLabel } from "redux/authStateSlice";
import { ChangeOrderRequestActionType } from "utility/enums/change-order-request";

const useAccessRight = () => {
	const location = useLocation();
	const userMenuAccessRights: IMenuAccessRightModel[] = useSelector(
		(state: RootState) => state.authState.userMenuAccessRights
	);
	const loggedInUserDetail = useSelector(
		(state: RootState) => state.authState.userDetails
	); // fetch logged in user details from redux
	const organizationDetail: OrganizationDetail = useSelector(
		(state: RootState) => state.authState.organizationDetails
	);

	const hasAccessRight = (menuName: string, accessRightName = "") => {
		const index: number = userMenuAccessRights.findIndex(
			(a: IMenuAccessRightModel) => a.menuName === menuName
		);
		if (index > -1) {
			return userMenuAccessRights[index].accessRights.some(
				(a: AccessRight) => a.accessRightName === accessRightName && a.isRights
			);
		}
		return false;
	};

	// check if selected erpDbCompanyId exist or not for label Division or Region in glAccountSettings (organizationDetail)
	const checkIsDivisionRegionEnabled = (
		erpDbCompanyId: number | null,
		baseFieldName = defaultDivisionGridLabel
	) => {
		if (
			erpDbCompanyId &&
			erpDbCompanyId > 0 &&
			organizationDetail.glAccountSettings &&
			organizationDetail.glAccountSettings.length > 0
		) {
			const tempDetail = organizationDetail.glAccountSettings.find(
				(g) => g.name.toLowerCase() === baseFieldName.toLowerCase()
			);
			return (
				tempDetail && tempDetail.enableInErpDbCompanies.includes(erpDbCompanyId)
			);
		}
		return false;
	};

	const checkIsOrgUnitIncludedInGLFormat = (
		erpDbCompanyId: number | null,
		baseFieldName = defaultDivisionGridLabel
	) => {
		if (
			erpDbCompanyId &&
			erpDbCompanyId > 0 &&
			organizationDetail.glAccountSettings &&
			organizationDetail.glAccountSettings.length > 0
		) {
			const tempDetail = organizationDetail.glAccountSettings.find(
				(g) => g.name.toLowerCase() === baseFieldName.toLowerCase()
			);
			return tempDetail &&
				tempDetail.includeInGlFormatInErpDbCompanies &&
				tempDetail.includeInGlFormatInErpDbCompanies.includes(erpDbCompanyId)
				? true
				: false;
		}
		return false;
	};

	const checkAccess = (
		menuName: string,
		accessRightName: string,
		moduleName: string
	): boolean | string => {
		if (!hasAccessRight(menuName, accessRightName)) {
			window.history.replaceState(location.state, "");
			return `You do not have permission to ${accessRightName} ${moduleName.toLowerCase()} detail.`;
		}
		return true;
	};

	const hasPageAccess = (
		pageName: string,
		path: string,
		moduleName: string
	): string | boolean => {
		if (loggedInUserDetail.userTypeId === UserType.External) {
			if (!Shared.isExternalUserLoggedIn()) return false;
			switch (path) {
				case AppRoutings.SupplyChainManagementPortal:
					return true;
				case AppRoutings.CompanyProfile:
					return true;
				case AppRoutings.InvoiceUploadSummaryGrid:
				case AppRoutings.SupplierDocumentUpload:
				case AppRoutings.ExternalUserConfirmation:
				case AppRoutings.SupplierBidSummary:
				case AppRoutings.QuerySummary:
				case AppRoutings.SupplierBidDetail:
				case AppRoutings.VendorBidQuery:
				case AppRoutings.SupplierBillingPeriodDetail:
					return true;
				default:
					return false;
			}
		} else {
			const routeState = location.state as any;
			if (userMenuAccessRights.find((a) => a.url === path)) {
				const accessRightList = userMenuAccessRights.find(
					(a: IMenuAccessRightModel) => a.menuName === pageName
				)?.accessRights;
				return accessRightList &&
					accessRightList.some((i: AccessRight) => i.isRights)
					? true
					: `You do not have permission to access ${pageName} page.`;
			} else {
				let rightToBeChecked: string = "";
				switch (path) {
					case AppRoutings.WorkflowDefinition:
					case AppRoutings.WorkflowStepDefinition:
						rightToBeChecked =
							routeState && routeState.workflowId && routeState.workflowId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.ProgrammeDetail:
						rightToBeChecked =
							routeState &&
							routeState.programmeId &&
							routeState.programmeId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.InternalUserDetail:
						rightToBeChecked =
							routeState &&
							routeState.internalUserId &&
							routeState.internalUserId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.DepartmentDetail:
						rightToBeChecked =
							routeState &&
							routeState.departmentId &&
							routeState.departmentId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.DepartmentInstanceDetail:
						rightToBeChecked =
							routeState &&
							routeState.departmentId &&
							routeState.departmentId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.ItemDetail:
						rightToBeChecked =
							routeState && routeState.itemId && routeState.itemId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.ProcurementMethodDetail:
						rightToBeChecked =
							routeState &&
							routeState.procurementMethodId &&
							routeState.procurementMethodId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.SystemTemplateDefinition:
						rightToBeChecked =
							routeState &&
							routeState.systemTemplateId &&
							routeState.systemTemplateId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.RequisitionEntry:
						rightToBeChecked =
							routeState &&
							routeState.requisitionId &&
							routeState.requisitionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.RequisitionConversionToBid:
						rightToBeChecked =
							routeState && routeState.requisitionIdList
								? AccessRightName.View
								: AccessRightName.Convert;
						break;
					case AppRoutings.BidDetail:
						rightToBeChecked =
							routeState && routeState.bidId && routeState.bidId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.RequisitionEntryAction:
						// check access for AppRoutings.RequisitionEntryAction path, url will be "/requisition-entry/:actionType/:requisitionId/:requisitionStepHistoryId"
						rightToBeChecked = window.location.pathname.includes(
							`/requisition-entry/${RequisitionActionType.Edit}`
						)
							? AccessRightName.View
							: window.location.pathname.includes(
									`/requisition-entry/${RequisitionActionType.Delete}`
							  )
							? AccessRightName.Delete
							: "";
						break;
					case AppRoutings.BudgetChangeOrderEntryAction:
						// check access for AppRoutings.BudgetChangeOrderEntryAction path
						rightToBeChecked = window.location.pathname.includes(
							`/budget-change-order-detail/`
						)
							? AccessRightName.View
							: "";
						break;
					case AppRoutings.RequisitionConversionToBidAction:
						// check access for AppRoutings.RequisitionConversionToBidAction path, url will be "/requisition-conversion-to-bid/:actionType/:requisitionId/"
						rightToBeChecked = window.location.pathname.includes(
							`/requisition-conversion-to-bid/${ReqConversionToBidActionType.Convert}`
						)
							? AccessRightName.Convert
							: "";
						break;
					case AppRoutings.BidEntryAction:
						// check access for AppRoutings.BidEntryAction path, url will be "/bid-entry/:actionType/:bidId/:bidStepId"
						rightToBeChecked = window.location.pathname.includes(
							`/bid-entry/${BidActionType.Edit}`
						)
							? AccessRightName.View
							: "";
						break;
					case AppRoutings.BidView:
						// check access for AppRoutings.BidEntryAction path, url will be "/bid-entry/:actionType/:bidId"
						rightToBeChecked = window.location.pathname.includes(
							`/bid-entry/${BidActionType.View}`
						)
							? AccessRightName.View
							: "";
						break;
					case AppRoutings.AppDetail:
						rightToBeChecked =
							routeState && routeState.appId && routeState.appId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.DepartmentalProcurementPlanEntry:
						rightToBeChecked =
							routeState &&
							routeState.departmentalAppId &&
							routeState.departmentalAppId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					// case AppRoutings.PriceAndBeeSettingEntry:
					// 	rightToBeChecked =
					// 		routeState &&
					// 		routeState.priceAndBeeSettingId &&
					// 		routeState.priceAndBeeSettingId > -1
					// 			? AccessRightName.View
					// 			: AccessRightName.Add;
					// 	break;
					case AppRoutings.PerformanceEvaluationSettingDetail:
						rightToBeChecked =
							routeState &&
							routeState.performanceEvaluationDefinitionId &&
							routeState.performanceEvaluationDefinitionId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					// case AppRoutings.SupplierBillingPeriodDetail:
					// 	rightToBeChecked =
					// 		routeState &&
					// 		routeState.supplierBillingPeriodId &&
					// 		routeState.supplierBillingPeriodId > -1
					// 			? AccessRightName.View
					// 			: AccessRightName.Add;
					// 	break;
					case AppRoutings.RequisitionConfirmation:
					case AppRoutings.ContractConfirmation:
					case AppRoutings.SchedulerEventConfirmation:
					case AppRoutings.SchedulerEventModuleConfirmation:
					case AppRoutings.BudgetAllocationConfirmation:
					case AppRoutings.Dashboard:
					case AppRoutings.ProcureToPayDashboard:
					case AppRoutings.ContractManagementDashboard:
					case AppRoutings.ContractPOConfirmation:
					case AppRoutings.ContractVariationConfirmation:
					case AppRoutings.UserProfile: // No rights need to be checked for user profile page
					case AppRoutings.PriceAndBeeSettingEntry:
					case AppRoutings.BidCommitteeConfirmation:
					case AppRoutings.BidCommitteeActivate:
					case AppRoutings.ChangeOrderRequestEntryConfirmation:
					case AppRoutings.ItemConfirmation:
					case AppRoutings.BidConfirmation:
					case AppRoutings.ContractTermination:
					case AppRoutings.ContractArchived:
					case AppRoutings.BidCommitteeView:
					case AppRoutings.ProjectDetail:
					case AppRoutings.ProgrammeDetails:
					case AppRoutings.ProjectSummaryGrid:
					case AppRoutings.BidQueryConfirmation:
					case AppRoutings.SupplierBillingPeriodDetail:
					case AppRoutings.CSDDailyUpdateDetailedGrid:
					case AppRoutings.ContractManagementSettings:
					// case AppRoutings.BudgetSummaryGrid: //No right check for now
					case AppRoutings.RequisitionSettings:
						return true;
					case AppRoutings.NewContractEntry:
						rightToBeChecked =
							routeState && routeState.contractId && routeState.contractId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.NewContractEntryAction:
						// check access for AppRoutings.NewContractEntryAction path, url will be "/contract-entry/:actionType/:contractId/:contractStepId"
						rightToBeChecked = window.location.pathname.includes(
							`/contract-entry/${ContractActionType.Edit}`
						)
							? AccessRightName.View
							: window.location.pathname.includes(
									`/contract-entry/${ContractActionType.Delete}`
							  )
							? AccessRightName.Delete
							: "";
						break;
					case AppRoutings.BidConversionToContract:
					case AppRoutings.BidConversionToPO:
						return true; // TODO: change when working on Contract Management
					case AppRoutings.BidSpecificationMeeting:
					case AppRoutings.BidEvaluationMeeting:
					case AppRoutings.BidEvaluation:
					case AppRoutings.BidEvaluationDetail:
					case AppRoutings.BidApproval:
					case AppRoutings.BidPublishing:
					case AppRoutings.Bidding:
						return true; // TODO: change when working on Bid Step pages
					case AppRoutings.BidCommitteeDetail:
						rightToBeChecked =
							routeState &&
							routeState.bidCommitteeId &&
							routeState.bidCommitteeId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.BidQueries:
						rightToBeChecked =
							routeState && routeState.bidId && routeState.bidId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.BudgetAllocationSummaryGrid:
					case AppRoutings.BudgetAllocationDetail:
						rightToBeChecked =
							routeState &&
							routeState.budgetAllocationId &&
							routeState.budgetAllocationId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.ErpDbCompanyDetail:
						rightToBeChecked =
							routeState &&
							routeState.erpDbCompanyId &&
							routeState.erpDbCompanyId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.BudgetChangeOrderDetail:
						rightToBeChecked =
							routeState &&
							routeState.requisitionId &&
							routeState.requisitionId > -1
								? AccessRightName.View
								: AccessRightName.Update;
						break;
					case AppRoutings.BudgetAllocationDetailAction:
						// check access for AppRoutings.BudgetAllocationDetailAction path, url will be "/budget-allocation-detail/:actionType/:budgetAllocationId/:budgetAllocationStepId/:budgetAllocationStepDepartmentId"
						rightToBeChecked = window.location.pathname.includes(
							`/budget-allocation-detail/${BudgetAllocationActionType.Edit}`
						)
							? AccessRightName.View
							: "";
						break;
					case AppRoutings.GlAccountDetail:
						rightToBeChecked =
							routeState &&
							routeState.glAccountId &&
							routeState.glAccountId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.BudgetingProcessDashboard:
					case AppRoutings.FinancialPerformanceDashboard:
					case AppRoutings.FixedAssetsTrackingDashboard:
					case AppRoutings.PerformanceEvaluationSummaryGrid:
						rightToBeChecked = AccessRightName.View;
						break;
					case AppRoutings.CompanyProfile:
					case AppRoutings.ExternalUsers:
						return true; //TODO: Check access rights when functionality is implemented
					case AppRoutings.ExpenditureSummaryGrid:
					case AppRoutings.ExpenditureDetail:
					case AppRoutings.PerformanceEvaluationDetail:
					case AppRoutings.PerformanceEvaluationHistoryDetail:
					case AppRoutings.AuditTrails:
					case AppRoutings.UserAccessAudit:
					case AppRoutings.DetailUserActivity:
					case AppRoutings.TransactionAudit:
						return true; //TODO: Check access rights when functionality is implemented
					case AppRoutings.ContractPOEntry:
						rightToBeChecked =
							routeState &&
							routeState.requisitionId &&
							routeState.requisitionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.ContractPOEntryAction:
						// check access for AppRoutings.ContractPOEntryAction path, url will be "/contract-po-entry/:actionType/:requisitionId/:requisitionStepHistoryId"
						rightToBeChecked = window.location.pathname.includes(
							`${AppRoutings.ContractPOEntry}/${RequisitionActionType.Edit}`
						)
							? AccessRightName.View
							: window.location.pathname.includes(
									`${AppRoutings.ContractPOEntry}/${RequisitionActionType.Delete}`
							  )
							? AccessRightName.Delete
							: "";
						break;
					case AppRoutings.ContractVariationEntry:
						rightToBeChecked =
							routeState &&
							routeState.requisitionId &&
							routeState.requisitionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.ContractVariationEntryAction:
						// check access for AppRoutings.ContractPOEntryAction path, url will be "/contract-variance-entry/:actionType/:requisitionId/:requisitionStepHistoryId"
						rightToBeChecked = window.location.pathname.includes(
							`${AppRoutings.ContractVariationEntry}/${RequisitionActionType.Edit}`
						)
							? AccessRightName.View
							: window.location.pathname.includes(
									`${AppRoutings.ContractVariationEntry}/${RequisitionActionType.Delete}`
							  )
							? AccessRightName.Delete
							: "";
						break;
					case AppRoutings.ChangeOrderRequestEntry:
						rightToBeChecked =
							routeState &&
							routeState.changeOrderRequestId &&
							routeState.changeOrderRequestId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.ChangeOrderRequestEntryAction:
						// check access for AppRoutings.ChangeOrderRequestEntryAction path, url will be "/change-order-request-entry/:actionType/:changeOrderRequestId/:changeOrderRequestStepId"
						rightToBeChecked = window.location.pathname.includes(
							`/change-order-request-entry/${ChangeOrderRequestActionType.Edit}`
						)
							? AccessRightName.View
							: window.location.pathname.includes(
									`/change-order-request-entry/${ChangeOrderRequestActionType.Delete}`
							  )
							? AccessRightName.Delete
							: "";
						break;
					case AppRoutings.DivisionDetail:
						rightToBeChecked =
							routeState && routeState.divisionId && routeState.divisionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.SubDivisionDetail:
						rightToBeChecked =
							routeState &&
							routeState.subDivisionId &&
							routeState.subDivisionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.RegionDetail:
						rightToBeChecked =
							routeState && routeState.regionId && routeState.regionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.SubRegionDetail:
						rightToBeChecked =
							routeState &&
							routeState.subRegionId &&
							routeState.subRegionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					case AppRoutings.SubDepartmentDetail:
						rightToBeChecked =
							routeState &&
							routeState.subRegionId &&
							routeState.subRegionId > -1
								? AccessRightName.View
								: AccessRightName.Add;
						break;
					default:
						break;
				}
				return rightToBeChecked
					? checkAccess(pageName, rightToBeChecked, moduleName)
					: `You do not have permission to access ${pageName} page.`;
			}
		}
	};

	return {
		hasAccessRight,
		hasPageAccess,
		checkIsDivisionRegionEnabled,
		checkIsOrgUnitIncludedInGLFormat,
	};
};

export default useAccessRight;
